import type React from 'react';
import { useSelector } from 'react-redux';

import { Anchor, Banner, CloseButton, TextButton } from '@zillow/constellation-10';
import { useAcceptRentAmountChangeWebApiHandler_acceptRentAmountChange } from '@zg-rentals/ts-rent-payment-client-react-query';

import { closeNotification } from '@common/actions/NotificationActions';
import { showRentAmountChange } from '@renter-hub/actions/onboarding';
import { MANUAL_FREQUENCY } from '@renter-hub/constants/paymentConstants';
import StringConstants from '@common/constants/StringConstants';
import PlaidButton from '@common/components/PlaidButton';
import { VERIFICATION_STATUS } from '@common/constants/PlaidRequirement';

type ReviewRentAmountChangeProps = {
  landlordFirstName: string;
};

type RentAmountChangeExpiredProps = {
  landlordFirstName: string;
  landlordEmail: string;
};

type VerifySingleBankAccountBannerProps = {
  paymenMethodStatus: string;
  accountLast4: string;
  plaidEnv: string;
  plaidLinkToken?: string;
  plaidLinkTokenExpiration?: number;
  getPlaidLinkToken: () => Promise<any>;
  onSuccess: (plaidToken: any) => Promise<void>;
  onExit: (error: any, metadata: any) => void;
};

type VerifyMultipleBankAccountsBannerProps = {
  onManagePaymentMethodsClick: () => void;
};

type CommonProps = {
  dispatch: (...args: Array<any>) => any;
};

const PaymentMethodDeleted = ({ paymentMethodDeleted }) => (
  <Banner tone="success" closeButton={<CloseButton onImpact />}>
    {`${paymentMethodDeleted} was successfully deleted.`}
  </Banner>
);

const NewPaymentMethodAdded = ({ bankName, needsVerification = false }) => {
  return (
    <Banner tone="success" closeButton={<CloseButton onImpact />}>
      {`${bankName} successfully added. ${
        needsVerification
          ? 'You will be able to use your account as soon as it is verified.'
          : 'You can now use this payment method.'
      }`}
    </Banner>
  );
};

const PaymentScheduleSuccessDetailed = ({ typeofPayment = MANUAL_FREQUENCY, dateOfTheMonth, date }) => (
  <Banner tone="success" closeButton={<CloseButton onImpact />}>
    {typeofPayment === MANUAL_FREQUENCY
      ? `Your one time payment has been scheduled for ${date}.`
      : `Your recurring payment has been scheduled for the ${dateOfTheMonth} of the month.`}
  </Banner>
);

const MicroDepositLimitReached = ({ dispatch }: CommonProps): React.ReactNode => (
  <Banner
    tone="warning"
    closeButton={<CloseButton onImpact />}
    actionButton={
      <TextButton
        onImpact
        onClick={() =>
          Promise.resolve()
            .then(() => dispatch(closeNotification()))
            .then(() => {
              if (typeof window !== 'undefined') {
                window.location.href = StringConstants.LINKS.FAQ_PAGE.zillow;
              }
            })
        }
      >
        contact support.
      </TextButton>
    }
  >
    You have exceeded the allowed number of attempts. Please&nbsp;
  </Banner>
);

const ReviewRentAmountChange = ({
  dispatch,
  landlordFirstName = 'Your landlord',
}: ReviewRentAmountChangeProps & CommonProps): React.ReactNode => (
  <Banner
    tone="warning"
    closeButton={<CloseButton onImpact />}
    actionButton={
      <TextButton
        onImpact
        onClick={() =>
          Promise.resolve()
            .then(() => dispatch(closeNotification()))
            .then(() => dispatch(showRentAmountChange()))
        }
      >
        Review and accept
      </TextButton>
    }
  >
    {landlordFirstName} has updated your rent amount.
  </Banner>
);

const RentAmountChangeExpired = ({
  dispatch,
  landlordEmail,
  landlordFirstName = 'your landlord',
}: RentAmountChangeExpiredProps & CommonProps): React.ReactNode => {
  const { mutateAsync: requestAcceptRentAmountChange } =
    useAcceptRentAmountChangeWebApiHandler_acceptRentAmountChange();

  const handleClose = async () => {
    await dispatch(closeNotification());
    await requestAcceptRentAmountChange({
      postData: { acceptExpiration: true },
    });
  };

  return (
    <Banner tone="warning" closeButton={<CloseButton onImpact />} onClose={handleClose}>
      An update to your rent amount has expired.{' '}
      <Anchor onImpact href={`mailto:${landlordEmail}`}>
        Get in touch with {landlordFirstName}
      </Anchor>{' '}
      about next steps.
    </Banner>
  );
};

const VerifySingleBankAccountBanner = ({
  paymenMethodStatus,
  accountLast4,
  plaidEnv,
  plaidLinkToken,
  plaidLinkTokenExpiration,
  getPlaidLinkToken,
  onSuccess,
  onExit,
}: VerifySingleBankAccountBannerProps): React.ReactNode => {
  const isPlaidRevoked = paymenMethodStatus === VERIFICATION_STATUS.PLAID_REVOKED;
  const userZuid = useSelector((state: any) => state.user?.userToken);
  return (
    <Banner tone={isPlaidRevoked ? 'warning' : 'info'} closeButton={<CloseButton onImpact />}>
      {isPlaidRevoked
        ? `We lost the verified connection to your bank account ending in ${accountLast4}.${' '}`
        : `Complete verification for your bank account ending in ${accountLast4}.${' '}`}
      <PlaidButton
        plaidEnv={plaidEnv}
        plaidLinkToken={plaidLinkToken}
        plaidLinkTokenExpiration={plaidLinkTokenExpiration}
        getPlaidLinkToken={getPlaidLinkToken}
        label={isPlaidRevoked ? 'Reconnect with Plaid' : 'Verify bank account'}
        onSuccess={onSuccess}
        onExit={onExit}
        dataTest="verify-bank-account-plaid-button"
        useConstellationButton={true}
        variant="text"
        size="sm"
        full={false}
        userZuid={userZuid}
      />
    </Banner>
  );
};

const VerifyMultipleBankAccountsBanner = ({
  onManagePaymentMethodsClick,
}: VerifyMultipleBankAccountsBannerProps): React.ReactNode => (
  <Banner tone="info" closeButton={<CloseButton onImpact />}>
    Multiple bank accounts need attention.&nbsp;
    <TextButton css={{ marginLeft: 'token(spacing.sm)' }} onImpact onClick={onManagePaymentMethodsClick}>
      Manage payment methods
    </TextButton>
  </Banner>
);

export {
  MicroDepositLimitReached,
  ReviewRentAmountChange,
  RentAmountChangeExpired,
  PaymentMethodDeleted,
  NewPaymentMethodAdded,
  PaymentScheduleSuccessDetailed,
  VerifySingleBankAccountBanner,
  VerifyMultipleBankAccountsBanner,
};
